import React, { useEffect, useState, useRef } from "react";
import { MapPin, Clock, Banknote, LogOut, Check, Calendar, Plus, RotateCcw, User, Send, MessageSquare, XCircle, FileText } from "lucide-react";
import {
  cleanerRegister, cleanerLogin, cleanerMe, cleanerJobs, cleanerMyJobs,
  cleanerAccept, cleanerRelease, cleanerSetFirstVisit, cleanerLog, cleanerMessage,
  cleanerGetMessages, cleanerSupportMessage, cleanerSetAhv, cleanerSetAvailability,
  cleanerMarkRead, cleanerJobRead, cleanerContractPdf, unreadCount,
  cleanerForgotPassword, cleanerCheckReset, cleanerResetPassword,
  getCleanerToken, setCleanerToken, clearCleanerToken,
} from "../api";

const errMsg = (e) => {
  const d = e?.response?.data?.detail;
  if (Array.isArray(d)) return d.map((x) => x.msg || "").join(" ");
  return d || e?.message || "Fehler";
};

const Field = (props) => (
  <input {...props} className="w-full h-12 rounded-xl border border-slate-200 px-4 text-[15px] focus:outline-none focus:border-teal-600 focus:ring-2 focus:ring-teal-100" />
);

const AuthView = ({ onAuthed }) => {
  const [mode, setMode] = useState("login");
  const [f, setF] = useState({ name: "", email: "", password: "", phone: "", city: "" });
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const [forgot, setForgot] = useState(false);
  const [forgotSent, setForgotSent] = useState(false);

  const submit = async () => {
    setErr(""); setBusy(true);
    try {
      const res = mode === "login"
        ? await cleanerLogin(f.email, f.password)
        : await cleanerRegister(f);
      setCleanerToken(res.token);
      onAuthed(res.cleaner);
    } catch (e) { setErr(errMsg(e)); }
    setBusy(false);
  };

  const sendForgot = async () => {
    setErr(""); setBusy(true);
    try { await cleanerForgotPassword(f.email); setForgotSent(true); }
    catch (e) { setErr(errMsg(e)); }
    setBusy(false);
  };

  if (forgot) {
    return (
      <div className="max-w-md mx-auto px-5 pt-28 md:pt-32 pb-16">
        <h1 className="font-head font-extrabold text-3xl text-slate-900 mb-1">Passwort vergessen</h1>
        <p className="text-slate-500 mb-6">Gib deine E-Mail ein – wir senden dir einen Link zum Zurücksetzen.</p>
        {forgotSent ? (
          <div data-testid="cp-forgot-sent" className="bg-teal-50 border border-teal-100 text-teal-800 rounded-2xl p-5">
            Wenn ein Konto mit dieser E-Mail existiert, haben wir dir gerade einen Link zum Zurücksetzen geschickt. Bitte prüfe dein Postfach.
          </div>
        ) : (
          <div className="space-y-3">
            <Field data-testid="cp-forgot-email" type="email" placeholder="E-Mail" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} />
            {err && <div data-testid="cp-error" className="text-sm text-red-600">{err}</div>}
            <button data-testid="cp-forgot-submit" disabled={busy || !f.email} onClick={sendForgot} className="w-full h-12 rounded-xl bg-teal-700 text-white font-head font-bold hover:bg-teal-800 disabled:opacity-50">
              {busy ? "Bitte warten…" : "Link senden"}
            </button>
          </div>
        )}
        <button data-testid="cp-forgot-back" onClick={() => { setForgot(false); setForgotSent(false); setErr(""); }} className="mt-5 text-sm text-slate-500 hover:text-slate-900 font-medium">← Zurück zum Login</button>
      </div>
    );
  }

  return (
    <div className="max-w-md mx-auto px-5 pt-28 md:pt-32 pb-16">
      <h1 className="font-head font-extrabold text-3xl text-slate-900 mb-1">Putzfrau-Portal</h1>
      <p className="text-slate-500 mb-6">{mode === "login" ? "Melde dich an, um Aufträge zu sehen." : "Registriere dich kostenlos und sieh offene Aufträge."}</p>
      <div className="flex gap-2 mb-5">
        <button data-testid="cp-tab-login" onClick={() => setMode("login")} className={`flex-1 h-11 rounded-xl text-sm font-semibold ${mode === "login" ? "bg-teal-700 text-white" : "bg-slate-100 text-slate-600"}`}>Einloggen</button>
        <button data-testid="cp-tab-register" onClick={() => setMode("register")} className={`flex-1 h-11 rounded-xl text-sm font-semibold ${mode === "register" ? "bg-teal-700 text-white" : "bg-slate-100 text-slate-600"}`}>Registrieren</button>
      </div>
      <div className="space-y-3">
        {mode === "register" && <Field data-testid="cp-name" placeholder="Vor- und Nachname" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} />}
        <Field data-testid="cp-email" type="email" placeholder="E-Mail" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} />
        <Field data-testid="cp-password" type="password" placeholder="Passwort" value={f.password} onChange={(e) => setF({ ...f, password: e.target.value })} />
        {mode === "register" && <Field data-testid="cp-phone" placeholder="Telefon" value={f.phone} onChange={(e) => setF({ ...f, phone: e.target.value })} />}
        {mode === "register" && <Field data-testid="cp-city" placeholder="Einsatzgebiet / Stadt" value={f.city} onChange={(e) => setF({ ...f, city: e.target.value })} />}
        {err && <div data-testid="cp-error" className="text-sm text-red-600">{err}</div>}
        <button data-testid="cp-submit" disabled={busy} onClick={submit} className="w-full h-12 rounded-xl bg-teal-700 text-white font-head font-bold hover:bg-teal-800 disabled:opacity-50">
          {busy ? "Bitte warten…" : mode === "login" ? "Einloggen" : "Kostenlos registrieren"}
        </button>
        {mode === "login" && (
          <button data-testid="cp-forgot-link" onClick={() => { setForgot(true); setErr(""); }} className="w-full text-center text-sm text-slate-500 hover:text-teal-700 font-medium pt-1">Passwort vergessen?</button>
        )}
      </div>
    </div>
  );
};

const ResetView = ({ token, onDone }) => {
  const [state, setState] = useState("checking"); // checking | ready | invalid
  const [info, setInfo] = useState(null);
  const [pw, setPw] = useState("");
  const [pw2, setPw2] = useState("");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    cleanerCheckReset(token)
      .then((d) => { setInfo(d); setState("ready"); })
      .catch((e) => { setErr(errMsg(e)); setState("invalid"); });
  }, [token]);

  const submit = async () => {
    setErr("");
    if (pw.length < 6) { setErr("Das Passwort muss mindestens 6 Zeichen haben."); return; }
    if (pw !== pw2) { setErr("Die Passwörter stimmen nicht überein."); return; }
    setBusy(true);
    try {
      const res = await cleanerResetPassword(token, pw);
      setCleanerToken(res.token);
      onDone(res.cleaner);
    } catch (e) { setErr(errMsg(e)); }
    setBusy(false);
  };

  const isInvite = info?.purpose === "invite";
  return (
    <div className="max-w-md mx-auto px-5 pt-28 md:pt-32 pb-16">
      {state === "checking" && <div className="text-center text-slate-400">Link wird geprüft…</div>}
      {state === "invalid" && (
        <div data-testid="cp-reset-invalid" className="bg-red-50 border border-red-200 text-red-700 rounded-2xl p-5">
          {err || "Dieser Link ist ungültig oder abgelaufen."}
          <div className="mt-4"><a href="/putzfrau-portal" className="text-teal-700 font-semibold hover:underline">Zum Login</a></div>
        </div>
      )}
      {state === "ready" && (
        <>
          <h1 className="font-head font-extrabold text-3xl text-slate-900 mb-1">{isInvite ? "Willkommen bei MyCleaner.ch" : "Neues Passwort"}</h1>
          <p className="text-slate-500 mb-6">
            {isInvite ? `Hallo ${info?.name || ""}, lege dein Passwort fest und du bist sofort drin.` : "Wähle ein neues Passwort für dein Konto."}
          </p>
          <div className="space-y-3">
            <Field data-testid="cp-reset-pw" type="password" placeholder="Neues Passwort (min. 6 Zeichen)" value={pw} onChange={(e) => setPw(e.target.value)} />
            <Field data-testid="cp-reset-pw2" type="password" placeholder="Passwort wiederholen" value={pw2} onChange={(e) => setPw2(e.target.value)} />
            {err && <div data-testid="cp-reset-error" className="text-sm text-red-600">{err}</div>}
            <button data-testid="cp-reset-submit" disabled={busy} onClick={submit} className="w-full h-12 rounded-xl bg-teal-700 text-white font-head font-bold hover:bg-teal-800 disabled:opacity-50">
              {busy ? "Bitte warten…" : isInvite ? "Passwort festlegen & einloggen" : "Passwort speichern"}
            </button>
          </div>
        </>
      )}
    </div>
  );
};

const JobWish = ({ job }) => (job.days?.length > 0 || job.time_window || job.start_date) ? (
  <div data-testid={`cp-job-wish-${job.id}`} className="text-sm text-slate-500 -mt-2 mb-4">
    Kundenwunsch: {[job.days?.join(", "), job.time_window, job.start_date && `ab ${job.start_date.split("-").reverse().join(".")}`].filter(Boolean).join(" · ")}
  </div>
) : null;

const JobCard = ({ job, onAccept, busy }) => (
  <div data-testid={`cp-job-${job.id}`} className="bg-white rounded-2xl border border-slate-100 p-5">
    <div className="flex items-center gap-4 text-sm text-slate-600 mb-3 flex-wrap">
      <span className="inline-flex items-center gap-1.5 font-semibold text-slate-900"><MapPin className="w-4 h-4 text-teal-600" /> {job.city}</span>
      {job.in_my_area && <span data-testid={`cp-job-area-${job.id}`} className="inline-flex items-center gap-1 bg-teal-50 text-teal-700 text-xs font-semibold px-2 py-0.5 rounded-full">In deinem Gebiet</span>}
      <span className="inline-flex items-center gap-1.5"><Clock className="w-4 h-4" /> {job.hours} Std.{job.freq ? ` · ${job.freq}` : ""}</span>
      <span className="inline-flex items-center gap-1.5 text-teal-700 font-semibold"><Banknote className="w-4 h-4" /> {job.pay_per_hour} CHF/Std. · ca. {job.est_pay} CHF</span>
      {job.pets && <span className="text-amber-600 font-medium">Haustiere</span>}
      {job.laundry && <span className="text-sky-600 font-medium">Mithilfe Wäsche</span>}
    </div>
    {job.tasks?.length > 0 && <div className="text-sm text-slate-500 mb-4">Aufgaben: {job.tasks.join(", ")}</div>}
    <JobWish job={job} />
    <button data-testid={`cp-accept-${job.id}`} disabled={busy} onClick={() => onAccept(job.id)} className="inline-flex items-center gap-2 bg-teal-700 text-white font-semibold px-5 py-2.5 rounded-xl hover:bg-teal-800 disabled:opacity-50">
      <Check className="w-4 h-4" /> Auftrag annehmen
    </button>
  </div>
);

const MyJobCard = ({ job, onFirstVisit, onLog, onRelease }) => {
  const [date, setDate] = useState(job.first_visit_date || "");
  const [weekday, setWeekday] = useState(job.shift_weekday || "");
  const [start, setStart] = useState(job.shift_start || "");
  const [end, setEnd] = useState(job.shift_end || "");
  const [log, setLog] = useState({ date: "", hours: "", location: "", note: "" });
  const [msgs, setMsgs] = useState(job.messages || []);
  const [mtext, setMtext] = useState("");
  const [msending, setMsending] = useState(false);
  const totalH = (job.worklogs || []).reduce((s, w) => s + (Number(w.hours) || 0), 0);
  const sendMsg = async () => {
    const t = mtext.trim();
    if (!t) return;
    setMsending(true);
    try { const r = await cleanerMessage(job.id, t); setMsgs(r.messages); setMtext(""); } catch (e) { /* noop */ }
    setMsending(false);
  };
  const unread = unreadCount(job.messages, job.read_at, "cleaner");
  useEffect(() => { if (unread > 0) cleanerJobRead(job.id).catch(() => {}); /* eslint-disable-next-line */ }, []);
  return (
    <div data-testid={`cp-myjob-${job.id}`} className="bg-white rounded-2xl border border-slate-100 p-5">
      <div className="flex items-center gap-4 text-sm text-slate-600 mb-3 flex-wrap">
        <span className="inline-flex items-center gap-1.5 font-semibold text-slate-900"><MapPin className="w-4 h-4 text-teal-600" /> {job.city}</span>
        <span className="inline-flex items-center gap-1.5"><Clock className="w-4 h-4" /> {job.hours} Std.{job.freq ? ` · ${job.freq}` : ""}</span>
        <span className="inline-flex items-center gap-1.5 text-teal-700 font-semibold"><Banknote className="w-4 h-4" /> {job.pay_per_hour} CHF/Std.</span>
        {job.cancelled && <span data-testid={`cp-cancelled-${job.id}`} className="inline-flex items-center gap-1 bg-red-50 text-red-600 text-xs font-semibold px-2 py-0.5 rounded-full"><XCircle className="w-3.5 h-3.5" /> Von Kundin abgesagt</span>}
      </div>
      <div className="bg-slate-50 rounded-xl p-3 text-sm text-slate-600 mb-4">
        <div className="font-semibold text-slate-800 mb-1 flex items-center gap-1.5"><User className="w-4 h-4" /> Kundenkontakt</div>
        <div>{job.customer?.name}</div>
        <div>
          {job.customer?.email && <a href={`mailto:${job.customer.email}`} className="text-teal-700 hover:underline">{job.customer.email}</a>}
          {job.customer?.phone && <> · <a href={`tel:${job.customer.phone}`} className="text-teal-700 hover:underline">{job.customer.phone}</a></>}
        </div>
        {job.customer?.address && <div>{job.customer.address}</div>}
        {!job.first_visit_date && !job.cancelled && (
          <div data-testid={`cp-contact-hint-${job.id}`} className="mt-2 bg-amber-50 border border-amber-200 text-amber-800 rounded-lg px-3 py-2 font-medium">
            Bitte nimm Kontakt mit der Kundin auf, um den ersten Einsatztag zu vereinbaren – und trage ihn danach unten ein.
          </div>
        )}
      </div>

      {/* Chat mit Kundin */}
      <div className="border border-slate-100 rounded-xl mb-4 overflow-hidden">
        <div className="px-3 py-2 bg-slate-50 text-sm font-semibold text-slate-700 flex items-center gap-1.5"><MessageSquare className="w-4 h-4" /> Nachrichten mit {job.customer?.name || "Kundin"}{unread > 0 && <span data-testid={`cp-job-unread-${job.id}`} className="inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 text-[10px] font-bold text-white bg-red-500 rounded-full">{unread}</span>}</div>
        <div data-testid={`cp-thread-${job.id}`} className="p-3 space-y-2 max-h-56 overflow-y-auto">
          {msgs.length === 0 && <div className="text-xs text-slate-400 text-center py-3">Noch keine Nachrichten. Schreib der Kundin z. B. zur Terminabsprache.</div>}
          {msgs.map((m) => (
            <div key={m.id} className={`flex ${m.sender === "cleaner" ? "justify-end" : "justify-start"}`}>
              <div className={`max-w-[80%] rounded-2xl px-3 py-2 text-sm ${m.sender === "cleaner" ? "bg-teal-700 text-white rounded-br-sm" : m.sender === "support" ? "bg-slate-900 text-white rounded-bl-sm" : "bg-slate-100 text-slate-800 rounded-bl-sm"}`}>
                <div className={`text-[10px] mb-0.5 ${m.sender === "cleaner" ? "text-teal-100" : m.sender === "support" ? "text-slate-300" : "text-slate-400"}`}>{m.sender === "cleaner" ? "Ich" : m.sender === "support" ? "MyCleaner Support" : (job.customer?.name || "Kundin")}</div>
                {m.text}
              </div>
            </div>
          ))}
        </div>
        {!job.cancelled && (
          <div className="p-2 border-t border-slate-100 flex items-center gap-2">
            <input data-testid={`cp-msg-input-${job.id}`} value={mtext} onChange={(e) => setMtext(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") sendMsg(); }} placeholder="Nachricht an Kundin…" className="flex-1 h-10 rounded-lg border border-slate-200 px-3 text-sm" />
            <button data-testid={`cp-msg-send-${job.id}`} disabled={msending || !mtext.trim()} onClick={sendMsg} className="inline-flex items-center gap-1 px-3 h-10 rounded-lg bg-teal-700 text-white text-sm font-semibold disabled:opacity-50"><Send className="w-4 h-4" /></button>
          </div>
        )}
      </div>

      <div className="mb-4">
        <label className="text-sm font-semibold text-slate-700 flex items-center gap-1.5 mb-1.5"><Calendar className="w-4 h-4" /> Erster Einsatz & Rhythmus</label>
        <div className="flex flex-wrap items-center gap-2">
          <input data-testid={`cp-fv-date-${job.id}`} type="date" value={date} onChange={(e) => { const v = e.target.value; setDate(v); if (v) { const dd = new Date(v + "T00:00:00"); const map = ["So","Mo","Di","Mi","Do","Fr","Sa"]; setWeekday(map[dd.getDay()] || ""); } }} className="h-11 rounded-xl border border-slate-200 px-3 text-sm" />
          <span className="text-sm text-slate-500">dann immer</span>
          <select data-testid={`cp-fv-weekday-${job.id}`} value={weekday} onChange={(e) => setWeekday(e.target.value)} className="h-11 rounded-xl border border-slate-200 px-2 text-sm">
            <option value="">Wochentag</option>
            {WEEKDAYS.map((d) => <option key={d} value={d}>{d}</option>)}
          </select>
          <span className="text-sm text-slate-500">von</span>
          <input data-testid={`cp-fv-start-${job.id}`} type="time" value={start} onChange={(e) => setStart(e.target.value)} className="h-11 rounded-xl border border-slate-200 px-2 text-sm" />
          <span className="text-sm text-slate-500">bis</span>
          <input data-testid={`cp-fv-end-${job.id}`} type="time" value={end} onChange={(e) => setEnd(e.target.value)} className="h-11 rounded-xl border border-slate-200 px-2 text-sm" />
          <button data-testid={`cp-fv-save-${job.id}`} onClick={() => onFirstVisit(job.id, { date, weekday, start, end })} className="px-4 h-11 rounded-xl bg-teal-700 text-white text-sm font-semibold">Speichern</button>
        </div>
        {job.first_visit_date && <div className="text-xs text-teal-700 mt-1">Gesetzt: ab {job.first_visit_date}{job.shift_weekday ? ` · immer ${job.shift_weekday}` : ""}{job.shift_start ? ` ${job.shift_start}–${job.shift_end || ""}` : ""}</div>}
      </div>

      <div className="border-t border-slate-100 pt-4">
        <div className="text-sm font-semibold text-slate-700 mb-2">Stunden erfassen (erfasst: {totalH} Std.)</div>
        {(job.worklogs || []).map((w) => (
          <div key={w.id} className="text-xs text-slate-500 mb-1">{w.date} · {w.hours} Std. · {w.location}{w.note ? ` · ${w.note}` : ""}</div>
        ))}
        <div className="grid grid-cols-2 gap-2 mt-2">
          <input data-testid={`cp-log-date-${job.id}`} type="date" value={log.date} onChange={(e) => setLog({ ...log, date: e.target.value })} className="h-10 rounded-lg border border-slate-200 px-2 text-sm" />
          <input data-testid={`cp-log-hours-${job.id}`} type="number" step="0.5" placeholder="Stunden" value={log.hours} onChange={(e) => setLog({ ...log, hours: e.target.value })} className="h-10 rounded-lg border border-slate-200 px-2 text-sm" />
          <input data-testid={`cp-log-loc-${job.id}`} placeholder="Wo genau (Adresse)" value={log.location} onChange={(e) => setLog({ ...log, location: e.target.value })} className="h-10 rounded-lg border border-slate-200 px-2 text-sm col-span-2" />
          <input placeholder="Notiz (optional)" value={log.note} onChange={(e) => setLog({ ...log, note: e.target.value })} className="h-10 rounded-lg border border-slate-200 px-2 text-sm col-span-2" />
        </div>
        <div className="flex items-center justify-between mt-3">
          <button data-testid={`cp-log-add-${job.id}`} onClick={() => onLog(job.id, log, () => setLog({ date: "", hours: "", location: "", note: "" }))} className="inline-flex items-center gap-1.5 bg-slate-900 text-white text-sm font-semibold px-4 py-2 rounded-lg"><Plus className="w-4 h-4" /> Eintrag hinzufügen</button>
          <button data-testid={`cp-release-${job.id}`} onClick={() => onRelease(job.id)} className="inline-flex items-center gap-1.5 text-sm text-slate-400 hover:text-red-600"><RotateCcw className="w-4 h-4" /> Auftrag absagen</button>
        </div>
      </div>
    </div>
  );
};

const SupportChat = ({ initial, readAt }) => {
  const [msgs, setMsgs] = useState(initial || []);
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const ref = useRef(null);
  const unread = unreadCount(initial, readAt, "cleaner");
  useEffect(() => { cleanerGetMessages().then((d) => setMsgs(d.messages || [])).catch(() => {}); if (unread > 0) cleanerMarkRead().catch(() => {}); /* eslint-disable-next-line */ }, []);
  useEffect(() => { if (ref.current) ref.current.scrollTop = ref.current.scrollHeight; }, [msgs.length]);
  const send = async () => {
    const t = text.trim();
    if (!t) return;
    setBusy(true);
    try { const r = await cleanerSupportMessage(t); setMsgs(r.messages); setText(""); } catch (e) { /* noop */ }
    setBusy(false);
  };
  return (
    <div className="mb-8 bg-white rounded-2xl border border-slate-100 overflow-hidden" data-testid="cp-support-chat">
      <div className="px-5 py-3 border-b border-slate-100 text-sm font-semibold text-slate-700 flex items-center gap-1.5"><MessageSquare className="w-4 h-4 text-teal-600" /> Nachrichten von MyCleaner{unread > 0 && <span data-testid="cp-support-unread" className="inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 text-[10px] font-bold text-white bg-red-500 rounded-full">{unread}</span>}</div>
      <div ref={ref} className="p-4 space-y-2 max-h-56 overflow-y-auto bg-slate-50/50">
        {msgs.length === 0 && <div className="text-xs text-slate-400 text-center py-3">Hier erreichst du direkt das MyCleaner-Team. Schreib uns bei Fragen.</div>}
        {msgs.map((m) => (
          <div key={m.id} className={`flex ${m.sender === "cleaner" ? "justify-end" : "justify-start"}`}>
            <div className={`max-w-[80%] rounded-2xl px-3 py-2 text-sm ${m.sender === "cleaner" ? "bg-teal-700 text-white rounded-br-sm" : "bg-slate-900 text-white rounded-bl-sm"}`}>
              <div className={`text-[10px] mb-0.5 ${m.sender === "cleaner" ? "text-teal-100" : "text-slate-300"}`}>{m.sender === "cleaner" ? "Ich" : "MyCleaner Support"}</div>
              {m.text}
            </div>
          </div>
        ))}
      </div>
      <div className="p-2 border-t border-slate-100 flex items-center gap-2">
        <input data-testid="cp-support-input" value={text} onChange={(e) => setText(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") send(); }} placeholder="Nachricht an MyCleaner…" className="flex-1 h-10 rounded-lg border border-slate-200 px-3 text-sm" />
        <button data-testid="cp-support-send" disabled={busy || !text.trim()} onClick={send} className="inline-flex items-center gap-1 px-3 h-10 rounded-lg bg-teal-700 text-white text-sm font-semibold disabled:opacity-50"><Send className="w-4 h-4" /></button>
      </div>
    </div>
  );
};

const WEEKDAYS = ["Mo", "Di", "Mi", "Do", "Fr", "Sa", "So"];

const AvailabilityCard = ({ cleaner }) => {
  const av = cleaner.availability || {};
  const [hours, setHours] = useState(av.weekly_hours || 0);
  const [days, setDays] = useState(av.days || []);
  const [note, setNote] = useState(av.note || "");
  const [saved, setSaved] = useState(false);
  const [busy, setBusy] = useState(false);
  const toggleDay = (d) => { setSaved(false); setDays((prev) => (prev.includes(d) ? prev.filter((x) => x !== d) : [...prev, d])); };
  const save = async () => {
    setBusy(true); setSaved(false);
    try { const c = await cleanerSetAvailability(Number(hours) || 0, days, note.trim()); const a = c.availability || {}; setHours(a.weekly_hours || 0); setDays(a.days || []); setNote(a.note || ""); setSaved(true); } catch (e) { /* noop */ }
    setBusy(false);
  };
  return (
    <div data-testid="cp-availability-card" className="bg-white border border-slate-200 rounded-2xl p-5 mb-6">
      <div className="flex items-center gap-2 mb-1">
        <Calendar className="w-4 h-4 text-teal-700" />
        <h2 className="font-head font-bold text-base text-slate-900">Meine Verfügbarkeit</h2>
      </div>
      <p className="text-sm text-slate-500 mb-3">Sag uns, wie viel du arbeiten kannst – so weisen wir dir passende Aufträge zu und sehen, wann du noch frei bist.</p>
      <div className="space-y-3">
        <div>
          <label className="text-xs font-semibold text-slate-500">Stunden pro Woche (gewünscht)</label>
          <input data-testid="cp-avail-hours" type="number" min="0" max="100" value={hours} onChange={(e) => { setHours(e.target.value); setSaved(false); }} className="mt-1 w-32 rounded-xl border border-slate-300 px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500" />
        </div>
        <div>
          <label className="text-xs font-semibold text-slate-500">An welchen Wochentagen kannst du arbeiten?</label>
          <div className="mt-1.5 flex flex-wrap gap-2">
            {WEEKDAYS.map((d) => (
              <button key={d} type="button" data-testid={`cp-avail-day-${d}`} onClick={() => toggleDay(d)} className={`w-11 h-10 rounded-xl text-sm font-semibold border transition-colors ${days.includes(d) ? "bg-teal-700 text-white border-teal-700" : "bg-white text-slate-600 border-slate-300 hover:border-teal-500"}`}>{d}</button>
            ))}
          </div>
        </div>
        <div>
          <label className="text-xs font-semibold text-slate-500">Notiz (optional, z. B. „nur vormittags")</label>
          <input data-testid="cp-avail-note" value={note} onChange={(e) => { setNote(e.target.value); setSaved(false); }} placeholder="z. B. nur vormittags, nicht am Wochenende" className="mt-1 w-full rounded-xl border border-slate-300 px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500" />
        </div>
        <button data-testid="cp-avail-save" onClick={save} disabled={busy} className="inline-flex items-center gap-1.5 bg-teal-700 hover:bg-teal-800 text-white font-semibold px-5 py-2.5 rounded-xl text-sm disabled:opacity-60">
          <Check className="w-4 h-4" /> Verfügbarkeit speichern
        </button>
        {saved && <div data-testid="cp-avail-saved" className="text-sm text-teal-700">Verfügbarkeit gespeichert. Danke!</div>}
      </div>
    </div>
  );
};

const AhvCard = ({ cleaner }) => {
  const [ahv, setAhv] = useState(cleaner.ahv_number || "");
  const [address, setAddress] = useState(cleaner.address || "");
  const [saved, setSaved] = useState(false);
  const [busy, setBusy] = useState(false);
  const save = async () => {
    setBusy(true); setSaved(false);
    try { const c = await cleanerSetAhv(ahv.trim(), address.trim()); setAhv(c.ahv_number || ""); setAddress(c.address || ""); setSaved(true); } catch (e) { /* noop */ }
    setBusy(false);
  };
  return (
    <div data-testid="cp-ahv-card" className="bg-white border border-slate-200 rounded-2xl p-5 mb-6">
      <div className="flex items-center gap-2 mb-1">
        <User className="w-4 h-4 text-teal-700" />
        <h2 className="font-head font-bold text-base text-slate-900">Angaben für den Arbeitsvertrag</h2>
      </div>
      <p className="text-sm text-slate-500 mb-3">Hinterlege deine Adresse und AHV-Nummer – wir füllen sie automatisch in deinen Arbeitsvertrag ein. Deinen Vertrag kannst du anschliessend unten herunterladen, unterschreiben und uns zurücksenden.</p>
      <div className="space-y-2">
        <div>
          <label className="text-xs font-semibold text-slate-500">Adresse (Strasse, PLZ, Ort)</label>
          <input
            data-testid="cp-address-input"
            value={address}
            onChange={(e) => { setAddress(e.target.value); setSaved(false); }}
            placeholder="Musterstrasse 12, 6300 Zug"
            className="mt-1 w-full rounded-xl border border-slate-300 px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
          />
        </div>
        <div>
          <label className="text-xs font-semibold text-slate-500">AHV-Nummer (Format 756.XXXX.XXXX.XX)</label>
          <div className="mt-1 flex flex-col sm:flex-row gap-2">
            <input
              data-testid="cp-ahv-input"
              value={ahv}
              onChange={(e) => { setAhv(e.target.value); setSaved(false); }}
              placeholder="756.1234.5678.90"
              className="flex-1 rounded-xl border border-slate-300 px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
            />
            <button data-testid="cp-ahv-save" onClick={save} disabled={busy} className="inline-flex items-center justify-center gap-1.5 bg-teal-700 hover:bg-teal-800 text-white font-semibold px-5 py-2.5 rounded-xl text-sm disabled:opacity-60">
              <Check className="w-4 h-4" /> Speichern
            </button>
          </div>
        </div>
      </div>
      {saved && <div data-testid="cp-ahv-saved" className="mt-2 text-sm text-teal-700">Angaben gespeichert. Danke! Sie erscheinen jetzt automatisch im Arbeitsvertrag.</div>}
      <div className="mt-4 pt-4 border-t border-slate-100">
        <p className="text-sm text-slate-500 mb-2">Dein Arbeitsvertrag (zum Prüfen & Unterschreiben):</p>
        <button data-testid="cp-contract-download" onClick={() => cleanerContractPdf().catch(() => {})} className="inline-flex items-center gap-1.5 bg-slate-900 hover:bg-slate-800 text-white font-semibold px-4 py-2.5 rounded-xl text-sm">
          <FileText className="w-4 h-4" /> Arbeitsvertrag herunterladen (PDF)
        </button>
      </div>
    </div>
  );
};

const Dashboard = ({ cleaner, onLogout }) => {
  const [jobs, setJobs] = useState([]);
  const [myJobs, setMyJobs] = useState([]);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState("");

  const load = async () => {
    try {
      const [j, m] = await Promise.all([cleanerJobs().catch(() => []), cleanerMyJobs().catch(() => [])]);
      setJobs(j); setMyJobs(m);
    } catch (e) { /* noop */ }
  };
  useEffect(() => { load(); }, []);

  const accept = async (id) => {
    setBusy(true); setMsg("");
    try { await cleanerAccept(id); setMsg("Auftrag angenommen! Der Kunde wurde informiert."); await load(); }
    catch (e) { setMsg(errMsg(e)); }
    setBusy(false);
  };
  const firstVisit = async (id, payload) => { try { await cleanerSetFirstVisit(id, payload); setMsg("Einsatz-Termin gespeichert."); await load(); } catch (e) { setMsg(errMsg(e)); } };
  const addLog = async (id, entry, reset) => {
    if (!entry.date || !entry.hours) { setMsg("Bitte Datum und Stunden angeben."); return; }
    try { await cleanerLog(id, { ...entry, hours: Number(entry.hours) }); reset(); setMsg("Stunden erfasst."); await load(); } catch (e) { setMsg(errMsg(e)); }
  };
  const release = async (id) => { if (!window.confirm("Auftrag wirklich absagen? Er wird wieder freigegeben.")) return; try { await cleanerRelease(id); await load(); } catch (e) { setMsg(errMsg(e)); } };

  return (
    <div className="max-w-3xl mx-auto px-5 pt-28 md:pt-32 pb-10">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="font-head font-extrabold text-2xl text-slate-900">Hallo {cleaner.name}</h1>
          <p className="text-sm text-slate-500">{cleaner.city ? `Einsatzgebiet: ${cleaner.city}` : "Putzfrau-Portal"}</p>
        </div>
        <button data-testid="cp-logout" onClick={onLogout} className="inline-flex items-center gap-1.5 text-slate-500 hover:text-slate-900 text-sm font-medium"><LogOut className="w-4 h-4" /> Abmelden</button>
      </div>

      {!cleaner.approved && (
        <div data-testid="cp-pending" className="bg-amber-50 border border-amber-200 rounded-2xl p-5 mb-6 text-amber-800">
          Dein Konto wurde erstellt und wartet auf <strong>Freigabe durch MyCleaner.ch</strong>. Sobald du freigegeben bist, siehst du hier offene Aufträge und kannst sie annehmen.
        </div>
      )}

      {msg && <div data-testid="cp-msg" className="mb-4 text-sm bg-teal-50 text-teal-800 border border-teal-100 rounded-xl px-4 py-2.5">{msg}</div>}

      <AvailabilityCard cleaner={cleaner} />

      <AhvCard cleaner={cleaner} />

      <SupportChat initial={cleaner.messages} readAt={cleaner.read_at} />

      {myJobs.length > 0 && (
        <div className="mb-8">
          <h2 className="font-head font-bold text-lg text-slate-900 mb-3">Meine Aufträge</h2>
          <div className="space-y-4">
            {myJobs.map((j) => <MyJobCard key={j.id} job={j} onFirstVisit={firstVisit} onLog={addLog} onRelease={release} />)}
          </div>
        </div>
      )}

      {cleaner.approved && (() => {
        const mine = jobs.filter((j) => j.in_my_area);
        const others = jobs.filter((j) => !j.in_my_area);
        return (
          <div>
            <h2 className="font-head font-bold text-lg text-slate-900 mb-3">Offene Aufträge ({jobs.length})</h2>
            {jobs.length === 0
              ? <div className="text-slate-400 bg-white border border-slate-100 rounded-2xl p-8 text-center">Aktuell keine offenen Aufträge. Du bekommst eine E-Mail, sobald ein neuer Job reinkommt.</div>
              : (
                <div className="space-y-6">
                  {mine.length > 0 && (
                    <div>
                      <div className="text-sm font-semibold text-teal-700 mb-2 flex items-center gap-1.5"><MapPin className="w-4 h-4" /> In deinem Einsatzgebiet{cleaner.city ? ` (${cleaner.city})` : ""}</div>
                      <div className="space-y-4">{mine.map((j) => <JobCard key={j.id} job={j} onAccept={accept} busy={busy} />)}</div>
                    </div>
                  )}
                  {others.length > 0 && (
                    <div>
                      {mine.length > 0 && <div className="text-sm font-semibold text-slate-500 mb-2">Weitere Aufträge in anderen Gebieten</div>}
                      <div className="space-y-4">{others.map((j) => <JobCard key={j.id} job={j} onAccept={accept} busy={busy} />)}</div>
                    </div>
                  )}
                </div>
              )}
          </div>
        );
      })()}
    </div>
  );
};

const CleanerPortal = () => {
  const [cleaner, setCleaner] = useState(null);
  const [checking, setChecking] = useState(true);
  const [resetToken, setResetToken] = useState(() => new URLSearchParams(window.location.search).get("reset") || "");

  useEffect(() => {
    if (resetToken) { setChecking(false); return; }
    if (!getCleanerToken()) { setChecking(false); return; }
    cleanerMe().then((c) => setCleaner(c)).catch(() => clearCleanerToken()).finally(() => setChecking(false));
  }, [resetToken]);

  const logout = () => { clearCleanerToken(); setCleaner(null); };
  const onReset = (c) => {
    // Token aus URL entfernen und ins Dashboard wechseln
    window.history.replaceState({}, "", window.location.pathname);
    setResetToken("");
    setCleaner(c);
  };

  if (checking) return <div className="py-20 text-center text-slate-400">Lädt…</div>;
  return (
    <div className="min-h-screen bg-slate-50">
      {resetToken && !cleaner
        ? <ResetView token={resetToken} onDone={onReset} />
        : cleaner ? <Dashboard cleaner={cleaner} onLogout={logout} /> : <AuthView onAuthed={setCleaner} />}
    </div>
  );
};

export default CleanerPortal;
